import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowRight,
  FaCode,
  FaLayerGroup,
  FaShareAlt,
  FaUserAstronaut,
} from 'react-icons/fa';
import NavbarHome from '../../components/NavbarHome';
import VihsMark from '../../components/VihsMark';
import { useAuth } from '../../contexts/AuthContext';
import './DevHub.css';

const pillars = [
  {
    icon: FaLayerGroup,
    title: 'Portfólio em categorias',
    text: 'Organize trabalhos por área — Roblox, UI, scripts, builds — com mídia e descrição clara.',
  },
  {
    icon: FaCode,
    title: 'Skills com domínio',
    text: 'Mostre o que você domina e em que nível, para quem visita o perfil entender seu perfil técnico.',
  },
  {
    icon: FaShareAlt,
    title: 'Presença compartilhável',
    text: 'Um link de perfil profissional para mandar em propostas, Discord e redes.',
  },
];

const steps = [
  ['Crie sua conta', 'Identidade única no ecossistema Vih\'s.'],
  ['Monte sua stack', 'Skills e níveis que representam seu trabalho.'],
  ['Publique no hub', 'Categorias e peças com imagem ou vídeo.'],
  ['Compartilhe', 'Use o perfil como cartão de visita digital.'],
];

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

export default function DevHub() {
  const { isAuthenticated, user } = useAuth();
  const primaryHref = isAuthenticated
    ? `/perfil/${user?.username || ''}`
    : '/entrar';
  const primaryLabel = isAuthenticated ? 'Abrir meu perfil' : 'Criar acesso';

  return (
    <div className="devhub-page">
      <NavbarHome />

      <section className="devhub-hero" aria-label="Vih's Dev Hub">
        <div className="devhub-hero-atmosphere" aria-hidden="true">
          <span className="devhub-hero-glow" />
          <span className="devhub-hero-grid" />
        </div>

        <div className="devhub-shell devhub-hero-inner">
          <motion.p
            className="devhub-kicker"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            Produto · Portfólio
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05 }}
          >
            Vih&apos;s Dev Hub
          </motion.h1>

          <motion.p
            className="devhub-lead"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.12 }}
          >
            O espaço para desenvolvedores e criadores montarem um portfólio vivo —
            skills, trabalhos e identidade técnica em um só perfil.
          </motion.p>

          <motion.div
            className="devhub-cta-row"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
          >
            <Link to={primaryHref} className="devhub-btn">
              {primaryLabel}
              <FaArrowRight aria-hidden="true" />
            </Link>
            {isAuthenticated ? (
              <Link to="/explorar" className="devhub-btn is-ghost">
                Explorar perfis
              </Link>
            ) : (
              <Link to="/entrar" className="devhub-btn is-ghost">
                Já tenho conta
              </Link>
            )}
          </motion.div>
        </div>
      </section>

      <section className="devhub-section" aria-labelledby="devhub-pillars-title">
        <div className="devhub-shell">
          <motion.div className="devhub-section-head" {...reveal}>
            <p className="devhub-kicker">Por que usar</p>
            <h2 id="devhub-pillars-title">Um hub feito para mostrar o seu trabalho</h2>
          </motion.div>

          <div className="devhub-pillar-grid">
            {pillars.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.title}
                  className="devhub-pillar"
                  {...reveal}
                  transition={{ ...reveal.transition, delay: index * 0.06 }}
                >
                  <span className="devhub-pillar-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="devhub-section is-alt" aria-labelledby="devhub-steps-title">
        <div className="devhub-shell">
          <motion.div className="devhub-section-head" {...reveal}>
            <p className="devhub-kicker">Começar</p>
            <h2 id="devhub-steps-title">Do zero ao portfólio em quatro passos</h2>
          </motion.div>

          <ol className="devhub-steps">
            {steps.map(([title, text], index) => (
              <motion.li key={title} {...reveal} transition={{ ...reveal.transition, delay: index * 0.05 }}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <section className="devhub-closing" aria-label="Chamada final">
        <div className="devhub-shell">
          <motion.div className="devhub-closing-panel" {...reveal}>
            <FaUserAstronaut className="devhub-closing-icon" aria-hidden="true" />
            <h2>Seu próximo trabalho merece um lugar certo</h2>
            <p>
              Monte o hub, publique o que importa e use o perfil como vitrine —
              simples, profissional e dentro do ecossistema Vih&apos;s.
            </p>
            <Link to={primaryHref} className="devhub-btn">
              {primaryLabel}
              <FaArrowRight aria-hidden="true" />
            </Link>
          </motion.div>
        </div>
      </section>

      <footer className="devhub-footer">
        <div className="devhub-shell devhub-footer-row">
          <Link to="/" className="devhub-footer-brand">
            <VihsMark />
            Vih&apos;s
          </Link>
          <p>© {new Date().getFullYear()} Vih&apos;s Dev Hub</p>
        </div>
      </footer>
    </div>
  );
}
